import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Paper,
  Box,
  useTheme,
  IconButton,
  Typography,
  CircularProgress,
  Card,
  useMediaQuery,
} from "@mui/material";
import { ExpandLess, ExpandMore, Grading, Person } from "@mui/icons-material";
import moment from "moment";
import applicantSchema from "./applicantSchema";
import {
  applicantGeneralFields,
  applicantTestFields,
} from "./applicantFieldConfigs";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { useApplicantData } from "./useApplicantData";
import {
  createApplicant,
  updateApplicant,
} from "../../reducers/ApplicantsReducer";
import FormSection from "../../components/Form/FormSection";
import { PATH_APPLICANTS } from "../../utils/routeConstants";
import PerNotesTable from "../../components/PerNotes/PerNotesTable";
import { perNotesOptions, PerPhotoType, PerTagType } from "../../utils/enums";
import TagsTable from "../../components/Tags/TagsTable";
import CityDialog from "../../components/CityDialog";
import PhotosTable from "../../components/PerPhotos/PhotoTable";
import Slider from "react-slick";
import { formatToBase64ImageUrl, printAMSReport } from "../../utils/functions";
import { areAllPermissionsDisabled, formatModulePermission, getAccessPermission, getModuleAccessPermission } from "../../reducers/PermissionsReducer";
import { notify } from "../../reducers/NotifierReducer";
import { AMSReportID } from "../../utils/constants";
import PrintBase64Pdf from "../../components/PrintBase64Pdf";
import PATPrintButton from "../../components/Buttons/PATPrintButton";

const ApplicantAddEditForm = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const readOnly = queryParams.get("readOnly") === "true";

  useEffect(() => {
    if (location.search && queryParams.get("readOnly") !== "true") {
      dispatch(notify("The requested page does not exist.", "warning"));
      history.push(PATH_APPLICANTS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search, history]);

  const [allExpanded, setAllExpanded] = useState(false);
  const [openCityDialog, setOpenCityDialog] = useState(false);
  const [printReport, setPrintReport] = useState(false);
  const [reportString, setReportString] = useState("");

  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const cityOptions = useMemo(() => {
    return (dictionary.codeCities || []).map((g) => ({
      label: g.State ? `${g.CityDescription}, ${g.State}` : g.CityDescription,
      value: g.CityDescription,
      state: g.State,
    }));
  }, [dictionary.codeCities]);

  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  const applicantPermissions = getModuleAccessPermission("applicants", "any");

  const applicantModulePermissions = formatModulePermission(
    "ApplicantsModule",
    applicantPermissions,
    ["Applicant Record"]
  );
  const isDisabledModule = areAllPermissionsDisabled(
    applicantModulePermissions["ApplicantsModule"]
  );

  const generalInfoTabPermission = getAccessPermission(
    "applicants",
    "General Info",
    "any"
  );
  const generalInfoTabReadPermission = generalInfoTabPermission?.Read ?? true;
  const generalInfoTabEditPermission = generalInfoTabPermission?.Edit ?? true;

  const additionalInfoTabPermission = getAccessPermission(
    "applicants",
    "Additional Info",
    "any"
  );
  const additionalInfoReadTabPermission =
    additionalInfoTabPermission?.Read ?? true;
  const additionalInfoEditTabPermission =
    additionalInfoTabPermission?.Edit ?? true;

  const tagTabPermission = getAccessPermission("applicants", "Tags", "any");
  const tagTabReadPermission = tagTabPermission?.Read ?? true;

  const noteTabPermission = getAccessPermission("applicants", "Notes", "any");
  const noteTabReadPermission = noteTabPermission?.Read ?? true;

  const photoTabPermission = getAccessPermission("applicants", "Photos", "any");
  const photoTabReadPermission = photoTabPermission?.Read ?? true;

  const handleToggleAll = () => {
    setAllExpanded((prev) => !prev);
  };

  const perNotes = useSelector((state) =>
    state.notes.notes && state.notes.notes["Applicants"]
      ? state.notes.notes["Applicants"][id]
      : []
  );

  const personnelTags = useSelector((state) =>
    state.personnelTags.personnelTags &&
    state.personnelTags.personnelTags[PerTagType.Applicant]
      ? state.personnelTags.personnelTags[PerTagType.Applicant][id]
      : []
  );

  const applicantPhotos = useSelector(
    (state) =>
      state.personnelPhotos.photosByEntity?.[PerPhotoType.Applicant]?.[id] ?? []
  );
  const isLoadingPhotos = useSelector((state) => state.personnelPhotos.loading);

  const thumbnailPhoto = applicantPhotos.find((photo) => photo.isThumbnail);

  let thumbnailSrc = null;
  if (thumbnailPhoto?.imageData) {
    const base64 =
      typeof thumbnailPhoto.imageData === "string"
        ? thumbnailPhoto.imageData
        : btoa(
            new Uint8Array(
              thumbnailPhoto.imageData?.data || thumbnailPhoto.imageData
            ).reduce((data, byte) => data + String.fromCharCode(byte), "")
          );

    thumbnailSrc = `data:image/jpeg;base64,${base64}`;
  }

  const photos = useSelector((state) =>
    (
      state.personnelPhotos.photosByEntity?.[PerPhotoType.Applicant]?.[id] || []
    ).sort((a, b) => {
      if (a.isThumbnail && !b.isThumbnail) return -1;
      if (!a.isThumbnail && b.isThumbnail) return 1;

      return new Date(b.createdAt) - new Date(a.createdAt);
    })
  );

  const hasPhotos = photos && photos.length > 0;

  const sliderSettings = {
    dots: true,
    infinite: photos.length > 1,
    speed: 500,
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const renderCardContent = () => {
    return (
      <Card
        style={{
          position: "relative",
          padding: 8,
          textAlign: "center",
          width: "250px",
          maxWidth: 600,
          height: "auto",
          marginLeft: isSmall ? 20 : 60,
        }}
      >
        <Box position="relative">
          {isLoadingPhotos ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={150}
            >
              <CircularProgress size={34} sx={{ mr: 1, ml: 1 }} />
            </Box>
          ) : hasPhotos ? (
            <Slider {...sliderSettings}>
              {photos.map((photo, index) => (
                <div key={index}>
                  <img
                    alt={`applicant-${index}`}
                    src={formatToBase64ImageUrl(photo.imageData)}
                    style={{
                      objectFit: "contain",
                      Width: "280px",
                      height: "120px",
                      margin: "auto",
                      display: "block",
                    }}
                  />
                </div>
              ))}
            </Slider>
          ) : (
            <Box
              sx={{
                Width: "280px",
                height: "120px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              No Preview
            </Box>
          )}
        </Box>
      </Card>
    );
  };

  const user = useSelector((state) => state.user);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(applicantSchema),
    defaultValues: {
      id: null,
      suffix: "",
      lastName: "",
      firstName: "",
      middleName: "",
      dob: "",
      ssn: "",
      ethnicity: "",
      gender: "",
      race: "",
      maritalStatus: "",
      phone: "",
      email: "",
      workPhoneNum: "",
      priorLawEnforcement: false,
      prepSchool: false,
      address: "",
      city: "",
      state: "",
      zip: "",
      comments: "",
      militaryRelatedNotes: "",
      education: "",
      pat: "",
      patDate: "",
      written: "",
      writtenDate: "",
      polygraph: "",
      polygraphDate: "",
      background: "",
      backgroundDateSent: "",
      backgroundDateReturned: "",
      oralCommittee: "",
      oralCommitteeDate: "",
      oralDirector: "",
      oralDirectorDate: "",
      eligibility: "",
      medical: "",
      medicalDate: "",
      medicalNotes: "",
      psych: "",
      psychDate: "",
      boxNum: "",
      yearApplied: "",
      status: "",
      testNotes: "",
      backgroundInvestigator: "",
      writtenNotes: "",
      patNotes: "",
      backgroundNotes: "",
      oralCommitteeNotes: "",
      oralDirectorNotes: "",
      polygraphNotes: "",
      psychNotes: "",
    },
  });

  const {
    genderOptions,
    ethnicityOptions,
    maritalOptions,
    stateOptions,
    statusOptions,
    applicantTestResultOptions,
    educationOptions,
    yearAppliedOptions,
    raceOptions,
    tagTypeOptions,
    suffixOptions,
    loaded,
  } = useApplicantData(id, reset);
  const options = {
    gender: genderOptions,
    ethnicity: ethnicityOptions,
    maritalStatus: maritalOptions,
    state: stateOptions,
    status: statusOptions,
    education: educationOptions,
    pat: applicantTestResultOptions,
    written: applicantTestResultOptions,
    polygraph: applicantTestResultOptions,
    background: applicantTestResultOptions,
    oralCommittee: applicantTestResultOptions,
    oralDirector: applicantTestResultOptions,
    medical: applicantTestResultOptions,
    psych: applicantTestResultOptions,
    yearApplied: yearAppliedOptions,
    race: raceOptions,
    city: cityOptions,
    suffix: suffixOptions,
  };

  const selectedCity = watch("city");
  useEffect(() => {
    if (selectedCity) {
      const matchedCity = cityOptions.find(
        (city) => city.value === selectedCity
      );
      if (matchedCity?.state) {
        setValue("state", matchedCity.state);
      }
    }
  }, [selectedCity, cityOptions, setValue]);

  const onSubmit = async (data) => {
    const cleanedData = {
      suffix: data.suffix || null,
      lastName: data.lastName.trim() || null,
      firstName: data.firstName.trim() || null,
      middleName: data.middleName.trim() || null,
      dob: data?.dob
        ? moment(data.dob, "MM/DD/YYYY").startOf("day").format("YYYY-MM-DD HH:mm:ss.SSS")
        : null,
      ssn: data.ssn.trim() || null,
      ethnicity: data.ethnicity || null,
      gender: data.gender || null,
      race: data.race || null,
      maritalStatus: data.maritalStatus || null,
      phone: data.phone.trim() || null,
      email: data.email ? data.email.trim() : null,
      workPhoneNum: data.workPhoneNum.trim() || null,
      priorLawEnforcement: data.priorLawEnforcement ?? null,
      prepSchool: data.prepSchool ?? null,
      address: data.address.trim() || null,
      city: data.city.trim() || null,
      state: data.state || null,
      zip: data.zip.trim() || null,
      comments: data.comments.trim() || null,
      militaryRelatedNotes: data.militaryRelatedNotes.trim() || null,
      education: data.education || null,
      pat: data.pat || null,
      patDate: data.patDate
        ? moment(data.patDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      patNotes: data.patNotes || null,
      written: data.written || null,
      writtenDate: data.writtenDate
        ? moment(data.writtenDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      writtenNotes: data.writtenNotes || null,
      polygraph: data.polygraph || null,
      polygraphDate: data.polygraphDate
        ? moment(data.polygraphDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      polygraphNotes: data.polygraphNotes || null,
      background: data.background || null,
      backgroundDateSent: data.backgroundDateSent
        ? moment(data.backgroundDateSent, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      backgroundDateReturned: data.backgroundDateReturned
        ? moment(data.backgroundDateReturned, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      backgroundNotes: data.backgroundNotes || null,
      backgroundInvestigator: data.backgroundInvestigator || null,
      oralCommittee: data.oralCommittee || null,
      oralCommitteeDate: data.oralCommitteeDate
        ? moment(data.oralCommitteeDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      oralCommitteeNotes: data.oralCommitteeNotes || null,
      oralDirector: data.oralDirector || null,
      oralDirectorDate: data.oralDirectorDate
        ? moment(data.oralDirectorDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      oralDirectorNotes: data.oralDirectorNotes || null,
      eligibility: data.eligibility ?? null,
      medical: data.medical || null,
      medicalDate: data.medicalDate
        ? moment(data.medicalDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      medicalNotes: data.medicalNotes ?? null,
      psych: data.psych || null,
      psychDate: data.psychDate
        ? moment(data.psychDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      psychNotes: data.psychNotes || null,
      boxNum: data.boxNum.trim() || null,
      yearApplied: data.yearApplied.trim() || null,
      status: data.status || null,
      testNotes: data.testNotes.trim() || null,
      agencyId: user.userAgency,
    };

    if (id) {
      await dispatch(updateApplicant(data.id, cleanedData));
    } else {
      await dispatch(createApplicant(cleanedData));
    }

    history.push(`${PATH_APPLICANTS}`);
  };

  if (!loaded) return null;

  const handleIconClick = () => {
    setOpenCityDialog(true);
  };

  const sectionProps = {
    control,
    errors,
    options,
    theme,
    handleIconClick,
  };

  const formSections = [
    generalInfoTabReadPermission && {
      title: "General Info",
      icon: <Person />,
      fields: applicantGeneralFields,
      allExpanded,
      defaultExpanded: true,
      editPermission: id ? !readOnly && generalInfoTabEditPermission : true,
    },
    additionalInfoReadTabPermission && {
      title: "Additional Info",
      icon: <Grading />,
      fields: applicantTestFields,
      allExpanded,
      editPermission: id ? !readOnly && additionalInfoEditTabPermission : true,
    },
  ].filter(Boolean);

  const handlePrintApplicantProfile = async () => {
      const result = await printAMSReport(
        AMSReportID.Applicants,
        id,
        user.userAgency,
        applicantModulePermissions,
        user.userData.user.Username
      );
  
      if (result) {
        setReportString(result?.base64 || "");
        setPrintReport(true);
      }
    };

  return (
    <>
      {loaded && (
        <Paper
          elevation={3}
          sx={{
            padding: theme.spacing(3),
            margin: theme.spacing(1),
            backgroundColor: theme.palette.background.paper,
            overflow: "hidden",
            height: "calc(100vh - 85px)",
            marginTop: theme.spacing(0.5),
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                onClick={handleToggleAll}
                title={allExpanded ? "Collapse All" : "Expand All"}
              >
                {allExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              {isLoadingPhotos ? (
                <CircularProgress size={24} sx={{ mr: 1, ml: 1 }} />
              ) : thumbnailSrc ? (
                <Box mr={2}>
                  <Box
                    component="img"
                    src={thumbnailSrc}
                    alt="Thumbnail"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #ccc",
                    }}
                  />
                </Box>
              ) : null}
              <Typography
                variant="h4"
                gutterBottom
                color={theme.palette.text.primary}
              >
                {id ? "Edit Applicant" : "Add Applicant"}
              </Typography>
            </Box>

            <Box display="flex" gap={2}>
              {!isDisabledModule && (
                <>
                  {id && (
                    <PATPrintButton
                      tooltipText="Print Applicant Profile"
                      color="primary"
                      onClick={handlePrintApplicantProfile}
                    />
                  )}
                  {!readOnly && (
                    <PATSaveButton
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit(onSubmit)}
                    />
                  )}
                </>
              )}
              <PATCloseButton
                tooltipText="Close"
                onClick={() => history.push(`${PATH_APPLICANTS}`)}
              />
            </Box>
          </Box>

          <Box sx={{ height: "70vh", overflowY: "auto", pr: 1 }}>
            <form>
              {formSections.map((section) => (
                <FormSection
                  key={section.title}
                  {...section}
                  {...sectionProps}
                  renderCardContent={renderCardContent}
                  id={id}
                />
              ))}
              {id ? (
                <>
                  {tagTabReadPermission && (
                    <TagsTable
                      id={id}
                      tagTypeOptions={tagTypeOptions}
                      personnelTags={personnelTags}
                      theme={theme}
                      allExpanded={allExpanded}
                      perTagType={PerTagType.Applicant}
                      readOnly={readOnly}
                    />
                  )}
                  {noteTabReadPermission && (
                    <PerNotesTable
                      noteFor={perNotesOptions.Applicants}
                      entityId={id}
                      perNotes={perNotes}
                      theme={theme}
                      allExpanded={allExpanded}
                      readOnly={readOnly}
                    />
                  )}
                  {photoTabReadPermission && (
                    <PhotosTable
                      id={id}
                      personnelPhotos={applicantPhotos}
                      theme={theme}
                      allExpanded={allExpanded}
                      photoFor={PerPhotoType.Applicant}
                      readOnly={readOnly}
                    />
                  )}
                </>
              ) : (
                <></>
              )}
            </form>
          </Box>
        </Paper>
      )}
      <CityDialog
        open={openCityDialog}
        onClose={() => {
          setOpenCityDialog(false);
        }}
      />
      <PrintBase64Pdf base64={reportString} open={printReport} />
    </>
  );
};

export default ApplicantAddEditForm;
